import React, { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import track, { useTracking } from "react-tracking";

// Azure
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";

import { setAdAuthUser } from "./slices/appSlice";
import { setCurrUser } from "./slices/sessionSlice";
import { useLookupsData } from "./context/LookupsContext";
import LogDataService from "./services/LogService";
import API, { setApiUnauthorizedHandler } from "./http-common";

// Layout/UI
import { PageLayout } from "./components/SiteLayout/PageLayout";
import { useDialogBox } from "./hooks/useDialogBox";

import { callApiToGetSpaCode } from "./utils/authUtils";
import {
	initializeSessionUsers,
	handleLogout,
	isUserLogin,
	isLogoutInProgress,
	loadUserProfile,
	applyRedirectTarget,
	Session,
} from "./utils/sessionUtils";
import { debugLog, errorLog } from "./utils/debugUtils";
import { reloadLookupsFromDB } from "./utils/appUtils";

const SESSION_EXPIRED_DIALOG_MS = 4500;
// Keep logout just after the auto-close window so users can read the message first.
const SESSION_EXPIRED_LOGOUT_DELAY_MS = SESSION_EXPIRED_DIALOG_MS + 100;
// Hybrid session guard: use route/focus probes for fast detection; keep background polling light.
const SESSION_GUARD_POLL_MS = 60000;

function App({ instance }) {
	const renderCount = useRef(0);
	renderCount.current++;

	const { trackEvent } = useTracking();
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const search = location.search;
	const getCode = new URLSearchParams(search).get("getCode");

	const { inProgress } = useMsal();

	// --- Local State --
	const [data, setData] = useState(null);
	const isOnTop = true;
	const [isDisplay, setIsDisplay] = useState("default");
	const forceLogoutOn401Ref = useRef(false);
	const forceLogoutTimerRef = useRef(null);

	// Custom hooks
	const { reloadLookups } = useLookupsData();
	const { showDialog } = useDialogBox();
	const syncEffectiveAccountStatus = useCallback(
		(user) => {
			const userId = Number(user?.id);
			const statusId = Number(user?.status_id);
			if (!Number.isSafeInteger(userId) || ![1, 0, -1].includes(statusId)) return;

			// /me reports only the effective identity. Match it before patching Redux so loginUser is untouched.
			const currUser = Session()?.currUser || {};
			if (Number(currUser.id) !== userId) return;
			dispatch(setCurrUser({ ...currUser, status_id: statusId }));
		},
		[dispatch]
	);
	// Keep the latest dialog function without re-registering the 401 handler effect.
	// If the effect depended on showDialog directly, re-renders could trigger cleanup,
	// clear the pending logout timer, and prevent redirect/logout from running.
	const showDialogRef = useRef(showDialog);

	// Global API guard: if backend says session is unauthenticated, force a frontend logout path.
	useEffect(() => {
		showDialogRef.current = showDialog;
	}, [showDialog]);

	useEffect(() => {
		// Register once so the handler/timer lifecycle survives normal app re-renders.
		// This ensures a 401-triggered logout timer is not canceled before it fires.
		setApiUnauthorizedHandler(() => {
			// Guard duplicate handler invocations and skip when app already considers user logged out.
			if (forceLogoutOn401Ref.current) return;
			if (!isUserLogin()) return;
			// Manual sign-out can race with auth probes; do not replace that UX with session-expired messaging.
			if (isLogoutInProgress()) return;
			forceLogoutOn401Ref.current = true;
			// Use one path for both manual close and auto-timeout logout so cleanup is consistent.
			const forceLogoutNow = () => {
				if (forceLogoutTimerRef.current) {
					clearTimeout(forceLogoutTimerRef.current);
					forceLogoutTimerRef.current = null;
				}
				handleLogout({ immediateRedirect: true });
			};
			showDialogRef.current?.({
				title: "Session Expired",
				message: [
					"Your session is no longer active.",
					"This can happen after a server restart or extended inactivity.",
					"You will be redirected to sign in again.",
				],
				type: "error",
				autoClose: true,
				autoCloseDelay: SESSION_EXPIRED_DIALOG_MS,
			}, forceLogoutNow);

			// Give users a brief readable message before the mandatory logout redirect.
			forceLogoutTimerRef.current = setTimeout(() => {
				forceLogoutNow();
			}, SESSION_EXPIRED_LOGOUT_DELAY_MS);
		});
		// Clear handler on unmount to prevent stale closures during hot reload/navigation remounts.
		return () => {
			if (forceLogoutTimerRef.current) {
				clearTimeout(forceLogoutTimerRef.current);
				forceLogoutTimerRef.current = null;
			}
			setApiUnauthorizedHandler(null);
		};
	}, []);

	useEffect(() => {
		// The minimal /me route refreshes current identities even when page data is cached/public-readable.
		if (!isUserLogin() || forceLogoutOn401Ref.current || isLogoutInProgress()) return;
		API.get("/me")
			.then(({ data }) => syncEffectiveAccountStatus(data))
			.catch(() => {
				// The interceptor handles unauthenticated 401; exit-only 403 stays nonfatal for switch-back.
			});
	}, [location.pathname, location.search, syncEffectiveAccountStatus]);

	useEffect(() => {
		// Automatic session-loss detection without requiring click/navigation.
		// connect.sid is typically HttpOnly, so use the guarded /me route as the session probe.
		if (!isUserLogin() || isLogoutInProgress()) return;

		const probeSession = () => {
			// Stop probing once either forced logout or manual logout has already claimed the auth boundary.
			if (forceLogoutOn401Ref.current || isLogoutInProgress()) return;
			API.get("/me")
				.then(({ data }) => syncEffectiveAccountStatus(data))
				.catch(() => {
					// The interceptor handles unauthenticated 401; exit-only 403 stays nonfatal for switch-back.
				});
		};

		const pollSessionWhenActive = () => {
			if (document.visibilityState !== "visible") return;
			if (!document.hasFocus()) return;
			probeSession();
		};
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				probeSession();
			}
		};

		const id = setInterval(pollSessionWhenActive, SESSION_GUARD_POLL_MS);
		window.addEventListener("focus", probeSession);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			clearInterval(id);
			window.removeEventListener("focus", probeSession);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [syncEffectiveAccountStatus]);

	// --- Handlers ---

	const mouseClickHandler = () => {
		debugLog(3, "[App][ACTION] mouseClickHandler", { isOnTop: isOnTop });
		if (!isOnTop) setIsDisplay("none");
		else setIsDisplay("default");
	};

	/**
	 * We render the SPA code that was acquired server-side, and provide it to the acquireTokenByCode API
	 * on the MSAL.js PublicClientApplication instance. The application should also render any account hints,
	 * as they will be needed for any interactive requests to ensure the same user is used for both requests.
	 * For more information about using loginHint and sid, visit:
	 * https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/lib/msal-core#2-login-the-user
	 */

	// STEP 1: Acquire token from Microsoft if ?code= is present and data is not set
	useEffect(() => {
		// Case: user is not logged in and not returning from MS login
		if (!getCode && !isUserLogin()) {
			// Store the user's original destination (e.g., /baseline/edit/123)
			sessionStorage.setItem("desiredPage", JSON.stringify(location));

			debugLog(2, "[App][FLOW] storeDesiredPage on mount", { location: location });
		}

		// Case: returning from Microsoft login with auth code
		// Handle SPA redirect with MSAL `code` exchange for access token
		const fetchTokenFromCode = async () => {
			if (!getCode || data) return; // No ?code param or token already set
			debugLog(2, "[App][FLOW] fetchTokenFromCode started", { getCode: getCode });

			// Call backend to extract SPA Auth Code
			let apiData = null;
			try {
				apiData = await trackPromise(callApiToGetSpaCode());
			} catch (err) {
				errorLog(2, "[App][FLOW] callApiToGetSpaCode failed", {
					error: err?.message || err,
				});
				showDialog({
					title: "Authentication Error",
					message: [
						"Unable to contact authentication service.",
						"Please try again. If the issue persists, contact support.",
					],
				});
				return;
			}

			if (!apiData || !apiData.code) {
				errorLog(2, "[App][FLOW] invalid SPA code response", { apiData });
				showDialog({
					title: "Authentication Error",
					message: [
						"Failed to retrieve authentication code from backend.",
						"Please try again later or contact support.",
					],
				});
				return;
			}

			const { code, loginHint } = apiData;

			// If no other MSAL interaction is in progress
			if (inProgress === "none") {
				try {
					// Attempt silent token acquisition using code
					const token = await trackPromise(
						instance.acquireTokenByCode({
							code, //Spa Auth code
						})
					);
					setData(token); // Sets `data` for next step
				} catch (error) {
					// If interactive login is required (e.g., session expired)
					// Falls back to loginPopup()
					if (error instanceof InteractionRequiredAuthError) {
						try {
							const token = await instance.loginPopup({
								loginHint, // Prefer loginHint claim over sid or preferredUsername
							});
							setData(token);
						} catch (popupError) {
							errorLog(2, "[App][FLOW] loginPopup failed after acquireTokenByCode", {
								error: popupError?.message || popupError,
							});
							showDialog({
								title: "Authentication Error",
								message: [
									"Unable to complete Microsoft sign-in.",
									"Please try again. If the issue persists, contact support.",
								],
							});
						}
					} else {
						errorLog(2, "[App][FLOW] acquireTokenByCode failed", {
							error: error?.message || error,
						});
						showDialog({
							title: "Authentication Error",
							message: [
								"Sign-in failed while processing the authentication code.",
								"Please try again. If the issue persists, contact support.",
							],
						});
					}
				}
			}
		};

		fetchTokenFromCode();
	}, [data, getCode, inProgress, instance, location, showDialog]);

	// STEP 2: Once token (data) is ready, update session and redirect
	useEffect(() => {
		// Once token is available, populate session and route user accordingly
		const handlePostLogin = async () => {
			if (!getCode || !data) return;

			const adAccount = data.account;
			debugLog(3, "[App][STATE] adAccount before dispatch", { adAccount });

			// Save to state and Redux with AD user info
			if (adAccount) {
				dispatch(setAdAuthUser(adAccount));
				debugLog(3, "[App][STATE] setAdAuthUser dispatched", {});
			}

			// Fallback if adAccount is unexpectedly null.
			if (!adAccount) {
				errorLog(2, "[App][FLOW] missing adAccount after token", { data: data });

				showDialog(
					{
						title: "Login Failed",
						message: ["Unable to retrieve account details. Please try again."],
					},
					() => handleLogout()
				);
				return;
			}
			// Track login analytics from AD account identity before profile hydration
			trackEvent({
				page: " - ",
				event: "userLogin",
				props: {
					email: adAccount?.username || null,
					userId: null,
					statusId: null,
					phase: "preHydration",
				},
			});

			debugLog(9, "[App][FLOW] token ready", { reduxSession: true, adAccount: adAccount });

			// Load internal user profile and roles from DB
			const profileResult = await trackPromise(loadUserProfile(adAccount.username, "both"));
			if (!profileResult?.ok) {
				errorLog(2, "[App][FLOW] loadUserProfile failed during login", {
					email: adAccount.username,
				});
				showDialog({
					title: "Authentication Error",
					message: profileResult.message
						? [profileResult.message]
						: [
								"Unable to load your profile after sign-in.",
								"Please try again. If the issue persists, contact support.",
							],
				});
				return;
			}
			// Track again after profile hydration so analytics includes internal user id/status
			const hydratedUser = profileResult.userProfile || Session().loginUser || {};
			trackEvent({
				page: " - ",
				event: "userLoginHydrated",
				props: {
					email: hydratedUser?.email || adAccount?.username || null,
					userId: hydratedUser?.id ?? null,
					statusId: hydratedUser?.status_id ?? null,
					phase: "postHydration",
				},
			});
			if (applyRedirectTarget(profileResult.redirectTarget)) {
				return;
			}

			const { loginUser } = Session();
			const parsedStatusId = Number(loginUser?.status_id);
			const hasValidStatusId =
				loginUser?.status_id !== "" &&
				loginUser?.status_id !== null &&
				typeof loginUser?.status_id !== "undefined" &&
				Number.isFinite(parsedStatusId);
			if (hasValidStatusId && parsedStatusId < 1) {
				// Handle inactive user
				showDialog(
					{
						title: "Information",
						message: [
							"Your account is inactive.",
							" ",
							"Please contact the Administrator for assistance, thank you.",
						],
					},
					() => handleLogout()
				);
			} else if (!loginUser?.full_name || loginUser.full_name === "") {
				// First-time login with no full name set
				showDialog(
					{
						title: "Information",
						message: [
							`Welcome! ${loginUser.email}.`,
							" ",
							"Please update your information on the 'My Profile' page, thank you.",
						],
					},
					() => window.location.replace(process.env.REACT_APP_URI + "/home")
				);
			} else {
				// Normal flow - navigate to original requested page or home
				const HOME_PATH = "/home";
				if (sessionStorage.hasOwnProperty("desiredPage")) {
					try {
						const desiredPage = JSON.parse(sessionStorage.desiredPage);

						debugLog(2, "[App][FLOW] redirecting to desiredPage", { desiredPage: desiredPage });

						const isHome = desiredPage.pathname === "/" && desiredPage.search === "";
						const targetPath = isHome
							? `${process.env.REACT_APP_URI}${HOME_PATH}`
							: `${process.env.REACT_APP_URI}${desiredPage.pathname}${desiredPage.search}`;

						debugLog(2, "[App][FLOW] redirect target computed", { targetPath });

						// Reload the page to get updated variables - ylee
						// ATTENTION!  this will clear out the console logs
						window.location.replace(targetPath);
					} catch (err) {
						errorLog(2, "[App][FLOW] failed to parse desiredPage", { error: err });
						window.location.replace(process.env.REACT_APP_URI + HOME_PATH);
					}
				} else {
					debugLog(2, "[App][FLOW] no desiredPage redirect to home", {});
					window.location.replace(process.env.REACT_APP_URI + HOME_PATH);
				}
			}
		};

		handlePostLogin();
	}, [data, dispatch, getCode, showDialog, trackEvent]);

	//================================================

	// --- Effect: Lookups auto-loading logic
	useEffect(() => {
		const resetApp = async () => {
			// Async/Await Wrapper version
			// More maintainable for longer logic - easier to extend later:
			try {
				debugLog(2, "[App][FLOW] resetApp started", { resetApp: true });
				await reloadLookupsFromDB(true);
				reloadLookups(); // Refresh all lookups across the app
				debugLog(2, "[App][FLOW] lookups reloaded", { resetApp: true });

				// Clear one-shot URL trigger after successful reset
				const params = new URLSearchParams(location.search);
				params.delete("resetApp");
				const nextSearch = params.toString();
				navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
			} catch (err) {
				errorLog(2, "[App][FLOW] failed to reload lookups", { error: err });
			}
		};

		const shouldReset = new URLSearchParams(location.search).get("resetApp") === "1";
		if (shouldReset) {
			resetApp();
		}
	}, [location.pathname, location.search, navigate, reloadLookups]);

	// Fallback: load lookups on first run if missing in sessionStorage
	useEffect(() => {
		const loadInitialLookups = async () => {
			if (!sessionStorage.hasOwnProperty("lookups")) {
				try {
					debugLog(2, "[App][FLOW] initial lookups load started", {});
					await reloadLookupsFromDB(false); // don't force reset, just load if missing
					reloadLookups();
				} catch (err) {
					errorLog(2, "[App][FLOW] failed to load initial lookups", { error: err });
				}
			}
		};
		loadInitialLookups();
	}, [reloadLookups]);

	// --- Effect: Create user session and configs
	useEffect(() => {
		initializeSessionUsers();
	}, []); // Runs only once after initial render

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[App][FLOW] render", { renderCount: renderCount.current });
	debugLog(9, "[App][STATE] render snapshot", {
		hasMsalInstance: !!instance,
		pathname: location.pathname,
		search: location.search,
		hasLookupsCache: sessionStorage.hasOwnProperty("lookups"),
		hasDesiredPage: sessionStorage.hasOwnProperty("desiredPage"),
		nodeEnv: process.env.NODE_ENV,
		reactAppEnv: process.env.REACT_APP_ENV || "n/a",
	});

	/**
	 * msal-react is built on the React context API and all parts of your app that require authentication must be
	 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
	 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
	 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
	 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
	 */

	return (
		<>
			<div onClick={mouseClickHandler}>
				<PageLayout isOnTop={isOnTop} isDisplay={isDisplay} />
			</div>
		</>
	);
}

export const TrackedApp = track(
	// app-level tracking data (dynamic, reads latest Redux session)
	() => {
		const session = Session();
		return {
			app: "Raven",
			userId: session?.loginUser?.id ?? null,
			asUserId:
				session?.currUser?.id && session.currUser.id !== session.loginUser?.id
					? session.currUser.id
					: null,
			ip: session?.ip ?? null,
		};
	},

	// top-level options
	{
		// custom dispatch to console.log in addition to pushing to dataLayer[]
		dispatch: (data) => {
			debugLog(2, "[App][FLOW] TrackedApp dispatch", { trackedApp: data });
			(window.dataLayer = window.dataLayer || []).push(data);

			// here is where you add your custom analytics call
			LogDataService.create(data).catch((error) => {
				errorLog(2, "[App][FLOW] LogDataService.create failed", {
					error: error?.message || error,
				});
			});
		},
	}
)(App);
